import { defineStore } from "pinia";
import { getListofEmployee, getListofEmployeesV2 } from "@/api";
import {
  getEmployees as getLocalStorageEmployees,
  updateEmployees,
} from "@components/helper/localStorageHelper";
import { ref, computed } from "vue";

export const useEmployeeStore = defineStore("employee", () => {
  const employees = ref([]);
  const tempEmployees = ref([]);
  const currentEmployee = ref(null);
  const activeEmployeesCount = ref(0);
  const totalEmployeesCount = ref(0);
  const isEmployeesFullyLoaded = ref(false);

  // Pagination state
  const pagination = ref({
    currentPage: 1,
    itemsPerPage: 10,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
    nextUrl: null,
    previousUrl: null,
  });

  const getEmployees = computed(() => employees.value);
  const getActiveEmployeesCount = computed(() => activeEmployeesCount.value);
  const getTotalEmployeesCount = computed(() => totalEmployeesCount.value);
  const getPagination = computed(() => pagination.value);

  function setCurrentEmployee(data) {
    currentEmployee.value = data;
  }

  function resetCurrentEmployee() {
    currentEmployee.value = null;
  }

  function setEmployees(data) {
    employees.value = data;
    // Since we're filtering by status=active on server side, all returned employees are active
    activeEmployeesCount.value = data.length;
    updateEmployees(data);
  }

  function setActiveEmployeesCount(data) {
    activeEmployeesCount.value = data;
  }

  function setTotalPositionsCount(count) {
    totalEmployeesCount.value = count;
  }

  function setPagination(paginationData) {
    pagination.value = {
      ...pagination.value,
      ...paginationData,
      totalPages: Math.ceil(
        paginationData.totalCount / paginationData.itemsPerPage
      ),
    };
  }

  async function loadEmployees(queryParams = {}) {
    try {
      // Set default pagination if not provided, but prioritize queryParams
      const defaultParams = {
        limit: pagination.value.itemsPerPage,
        offset:
          (pagination.value.currentPage - 1) * pagination.value.itemsPerPage,
      };

      // Merge queryParams, giving priority to queryParams values
      const finalParams = {
        ...defaultParams,
        ...queryParams,
      };

      setEmployees(getLocalStorageEmployees());
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
      let response = await getListofEmployeesV2({
        account_id: accountId,
        queryParamsObj: finalParams,
      });

      if (response.data?.count) {
        totalEmployeesCount.value = response.data.count;
      }

      if (response.data?.results) {
        updateEmployees(response.data.results); // localstorage
        setEmployees(response.data.results); // store

        // Update pagination info
        setPagination({
          totalCount: response.data.count || 0,
          hasNext: !!response.data.next,
          hasPrevious: !!response.data.previous,
          nextUrl: response.data.next,
          previousUrl: response.data.previous,
          itemsPerPage: finalParams.limit,
        });

        // Update total count for active employees
        totalEmployeesCount.value = response.data.count || 0;
      }
    } catch (e) {
      console.error("error fetching employees: ", e);
    }
  }

  async function loadEmployeesPage(
    page,
    itemsPerPage = pagination.value.itemsPerPage
  ) {
    setPagination({ currentPage: page, itemsPerPage });
    await loadEmployees();
  }

  async function loadNextPage() {
    if (
      pagination.value.hasNext &&
      pagination.value.currentPage < pagination.value.totalPages
    ) {
      await loadEmployeesPage(pagination.value.currentPage + 1);
    }
  }

  async function loadPreviousPage() {
    if (pagination.value.hasPrevious && pagination.value.currentPage > 1) {
      await loadEmployeesPage(pagination.value.currentPage - 1);
    }
  }

  async function loadEmployeesBatch(page = 1, size = 50) {
    try {
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
      const response = await getListofEmployee(accountId, page, size);

      if (response.data?.results) {
        const current = page === 1 ? [] : employees.value;
        const newList = [...current, ...response.data.results];
        updateEmployees(newList);
        setEmployees(newList);
      }

      if (response.data?.count) {
        totalEmployeesCount.value = response.data.count;
      }

      return response.data;
    } catch (e) {
      console.error("error fetching employees: ", e);
    }
  }

  async function fetchRemainingEmployeesInBatches() {
    const size = 50;
    const concurrency = 5;

    const total = totalEmployeesCount.value || 500;
    const alreadyLoaded = employees.value.length;
    const pagesAlreadyFetched = Math.ceil(alreadyLoaded / size);
    const totalPagesNeeded = Math.ceil(total / size);
    const pagesRemaining = totalPagesNeeded - pagesAlreadyFetched;

    if (pagesRemaining <= 0) {
      isEmployeesFullyLoaded.value = true;
      return;
    }

    tempEmployees.value = [];
    let currentPage = pagesAlreadyFetched + 1;

    while (currentPage <= totalPagesNeeded) {
      const requests = [];

      for (let i = 0; i < concurrency && currentPage <= totalPagesNeeded; i++) {
        requests.push(loadEmployeesBatchToTemp(currentPage, size));
        currentPage++;
      }

      await Promise.all(requests);
    }

    const allEmployees = [...employees.value, ...tempEmployees.value];
    setEmployees(allEmployees);
    updateEmployees(allEmployees);
    tempEmployees.value = [];

    isEmployeesFullyLoaded.value = true;
  }

  async function loadEmployeesBatchToTemp(page, size) {
    try {
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
      const response = await getListofEmployee(accountId, page, size);

      if (response.data?.results) {
        tempEmployees.value = [
          ...tempEmployees.value,
          ...response.data.results,
        ];
      }

      if (response.data?.count) {
        totalEmployeesCount.value = response.data.count;
      }

      return response.data;
    } catch (e) {
      console.error("error fetching employees: ", e);
    }
  }

  async function loadEmployeesFromServer() {
    try {
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
      const { data } = await getListofEmployee(accountId, 1, 500);
      return data?.results || [];
    } catch (e) {
      console.error("error fetching employees from server: ", e);
      return [];
    }
  }

  // Track ongoing requests to prevent duplicate API calls
  const pendingRequests = new Map();

  // Centralized method to fetch current employee data
  async function fetchCurrentEmployee(empId, forceRefresh = false) {
    try {
      // If we already have the current employee data and not forcing refresh, return it
      if (
        !forceRefresh &&
        currentEmployee.value &&
        currentEmployee.value.id == empId
      ) {
        return currentEmployee.value;
      }

      // Check if there's already a pending request for this employee
      if (pendingRequests.has(empId)) {
        // Wait for the existing request to complete
        return await pendingRequests.get(empId);
      }

      // Create a new request promise
      const requestPromise = (async () => {
        try {
          const accountId = JSON.parse(
            localStorage.getItem("currentAccountID")
          );

          // Import getEmployeeById dynamically to avoid circular dependency
          const { getEmployeeById } = await import("@/api");
          const response = await getEmployeeById(accountId, empId);

          if (response?.data) {
            setCurrentEmployee(response.data);
            // Also update Vuex store for backwards compatibility
            // This will be handled by the component that calls this method
            return response.data;
          }

          return null;
        } finally {
          // Remove the promise from pending requests once completed
          pendingRequests.delete(empId);
        }
      })();

      // Store the promise so concurrent calls can await it
      pendingRequests.set(empId, requestPromise);

      return await requestPromise;
    } catch (error) {
      // Remove from pending requests on error
      pendingRequests.delete(empId);
      console.error("Error fetching current employee:", error);
      // Check if it's a 404 error and throw it to allow proper error handling
      if (error.response && error.response.status === 404) {
        throw error;
      }
      return null;
    }
  }

  // Centralized method to recalculate employee readiness
  async function recalculateCurrentEmployeeReadiness(
    empId,
    forceRecalc = true
  ) {
    try {
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));

      // Import recalculateEmployeeReadiness dynamically to avoid circular dependency
      const { recalculateEmployeeReadiness } = await import("@/api");
      const response = await recalculateEmployeeReadiness(
        accountId,
        empId,
        forceRecalc
      );

      if (response?.data) {
        // Update current employee with new readiness data
        if (currentEmployee.value) {
          currentEmployee.value = {
            ...currentEmployee.value,
            current_readiness: response.data.current_readiness,
            needs_recalc: response.data.needs_recalc,
          };
        }
        return response.data;
      }

      return null;
    } catch (error) {
      console.error("Error recalculating employee readiness:", error);
      return null;
    }
  }

  return {
    employees,
    currentEmployee,
    activeEmployeesCount,
    totalEmployeesCount,
    isEmployeesFullyLoaded,
    pagination,
    getEmployees,
    getActiveEmployeesCount,
    getTotalEmployeesCount,
    getPagination,
    setCurrentEmployee,
    resetCurrentEmployee,
    setEmployees,
    setActiveEmployeesCount,
    setTotalPositionsCount,
    setPagination,
    loadEmployees,
    loadEmployeesPage,
    loadNextPage,
    loadPreviousPage,
    loadEmployeesFromServer,
    loadEmployeesBatch,
    fetchRemainingEmployeesInBatches,
    fetchCurrentEmployee,
    recalculateCurrentEmployeeReadiness,
  };
});
